// NOTE: this should roughly match the implementation of `applyNodeChanges`, here:
// https://github.com/xyflow/xyflow/blob/v11/packages/core/src/utils/changes.ts#L45
import { useSnackbar } from '@automata/rexports/notistack';
import { logger } from '@automata/utils';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Connection,
  Dimensions,
  Edge,
  EdgeChange,
  NodeChange,
  OnConnectStart,
  OnEdgeUpdateFunc,
} from 'reactflow';

import { TIME_CONSTRAINT_EDGE } from '../../../constants';
import {
  deleteNodesEdges,
  thunkCreateDataDependencyEdge,
  thunkCreateLabwareDependencyEdge,
} from '../../../features/actions';
import {
  createTimeConstraint,
  editTimeConstraintConnection,
  moveNodes,
  updateNodesEdgesSelection,
} from '../../../features/graph/graphSlice';
import {
  selectGraphNodes,
  selectSelectedEdges,
  selectSelectedNodeIds,
} from '../../../features/graph/selectors';
import { generateTimeConstraintId } from '../../../features/helpers';
import { selectAllEdges } from '../../../features/selectors';
import {
  changeSelectionDragState,
  setCurrentTimeConstraintConnectionInProgress,
  setNewlyCreatedTimeConstraintId,
} from '../../../features/ui/uiSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import type { GraphNodes, NodePosition } from '../../../types';
import {
  isDataConnection,
  isLabwareDependencyConnection,
  isTimeConstraintConnection,
  timeConstraintConnectionToPartialInstance,
} from '../../../utils';
import {
  parseTimeConstraintIdFromNodeId,
  toTimeConstraintNodeId,
} from '../nodes/TimeConstraintNode/utils';
import { TIME_CONSTRAINT_HANDLE_TYPE } from '../TimeConstraintHandle';
import {
  ConnectionType,
  getConnectionType,
  getTimeConstraint,
  validateConnection,
} from '../utils';

export const useCanvasHandlers = () => {
  const dispatch = useAppDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const edgeUpdateSuccessful = useRef<boolean>(true);

  const edges = useAppSelector(selectAllEdges);
  const nodes = useAppSelector(selectGraphNodes);
  const currentlySelectedEdges = useAppSelector(selectSelectedEdges);
  const currentlySelectedNodeIds = useAppSelector(selectSelectedNodeIds);

  const [draggingNodes, setDraggingNodes] = useState<
    Record<string, NodePosition>
  >({});
  const [dimensionsNodes, setDimensionsNodes] = useState<
    Record<string, Dimensions>
  >({});
  const [timeConstraintNodePositions, setTimeConstraintNodePositions] =
    useState<Record<string, NodePosition>>({});
  const [
    customPositionedTimeConstraintNodes,
    setCustomPositionedTimeConstraints,
  ] = useState<Record<string, true>>({});

  const setTimeConstraintNodePositionFromEdgeMidpoint = useCallback(
    (timeConstraintId: string, position: NodePosition) => {
      if (customPositionedTimeConstraintNodes[timeConstraintId]) {
        return;
      }

      setTimeConstraintNodePositions((prev) => {
        const existingPosition = prev[timeConstraintId];
        if (
          existingPosition !== undefined &&
          existingPosition.x === position.x &&
          existingPosition.y === position.y
        ) {
          return prev;
        }

        return {
          ...prev,
          [timeConstraintId]: position,
        };
      });
    },
    [customPositionedTimeConstraintNodes]
  );

  const transientEdges = useMemo(
    () =>
      edges.map((edge) => {
        if (edge.type !== TIME_CONSTRAINT_EDGE) {
          return edge;
        }

        return {
          ...edge,
          data: {
            ...edge.data,
            iconPosition:
              draggingNodes[toTimeConstraintNodeId(edge.id)] ??
              timeConstraintNodePositions[edge.id],
            onMidpointChange: setTimeConstraintNodePositionFromEdgeMidpoint,
          },
        };
      }),
    [
      edges,
      draggingNodes,
      timeConstraintNodePositions,
      setTimeConstraintNodePositionFromEdgeMidpoint,
    ]
  );

  // TODO IMMEDIATELY: rename? they're not "transient", they're "full"? Or something...
  const transientNodes: GraphNodes = useMemo(() => {
    const baseNodes = nodes.map((node) => {
      let componentStateData: {
        position?: NodePosition;
        dimensions?: Dimensions;
      } = {};

      if (node.id in draggingNodes) {
        componentStateData.position = draggingNodes[node.id];
      }
      if (node.id in dimensionsNodes) {
        componentStateData.dimensions = dimensionsNodes[node.id];
      }

      if (Object.keys(componentStateData).length > 0) {
        return { ...node, ...componentStateData };
      }
      return node;
    });

    const timeConstraintNodes = transientEdges
      .filter((edge) => edge.type === TIME_CONSTRAINT_EDGE)
      .flatMap((edge) => {
        if (edge.data === undefined) {
          return [];
        }

        const timeConstraintId = edge.id;
        const nodeId = toTimeConstraintNodeId(timeConstraintId);
        const position =
          draggingNodes[nodeId] ??
          timeConstraintNodePositions[timeConstraintId];
        const dimensions = dimensionsNodes[nodeId];

        return [
          {
            id: nodeId,
            type: 'timeConstraint' as const,
            position: position ?? { x: 0, y: 0 },
            selected: edge.selected,
            hidden: position === undefined,
            draggable: true,
            selectable: true,
            connectable: false,
            deletable: false,
            data: {
              errors: edge.data.errors,
              labwareCorrespondence: edge.data.labwareCorrespondence,
              timeConstraintId,
            },
            ...(dimensions !== undefined ? { dimensions } : {}),
          },
        ];
      });

    return [...baseNodes, ...timeConstraintNodes];
  }, [
    nodes,
    transientEdges,
    draggingNodes,
    timeConstraintNodePositions,
    dimensionsNodes,
  ]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      for (const change of changes) {
        switch (change.type) {
          case 'select': {
            const timeConstraintId = parseTimeConstraintIdFromNodeId(change.id);
            const isTimeConstraintNode = timeConstraintId !== null;

            if (isTimeConstraintNode) {
              if (change.selected) {
                dispatch(
                  updateNodesEdgesSelection({
                    edges: {
                      selected: [timeConstraintId],
                      deselected: currentlySelectedEdges.filter(
                        (edgeId) => edgeId !== timeConstraintId
                      ),
                    },
                    nodes: { selected: [], deselected: [] },
                  })
                );
              } else {
                dispatch(
                  updateNodesEdgesSelection({
                    edges: { selected: [], deselected: [timeConstraintId] },
                    nodes: { selected: [], deselected: [] },
                  })
                );
              }
            } else if (change.selected) {
              dispatch(
                updateNodesEdgesSelection({
                  nodes: { selected: [change.id], deselected: [] },
                })
              );
            } else {
              dispatch(
                updateNodesEdgesSelection({
                  nodes: { selected: [], deselected: [change.id] },
                })
              );
            }
            break;
          }
          case 'position': {
            const timeConstraintId = parseTimeConstraintIdFromNodeId(change.id);
            const isTimeConstraintNode = timeConstraintId !== null;

            if (change.dragging) {
              setDraggingNodes((prev) => ({
                ...prev,
                [change.id]: change.position as NodePosition,
              }));
            } else if (draggingNodes[change.id]) {
              const node = draggingNodes[change.id];
              if (!node) return;

              if (isTimeConstraintNode) {
                setTimeConstraintNodePositions((prev) => ({
                  ...prev,
                  [timeConstraintId]: node,
                }));
                setCustomPositionedTimeConstraints((prev) => ({
                  ...prev,
                  [timeConstraintId]: true,
                }));
              } else {
                dispatch(
                  moveNodes({
                    [change.id]: { position: node },
                  })
                );
              }
              setDraggingNodes((prev) => omit(prev, change.id));
            }
            break;
          }
          case 'dimensions':
            if (change.dimensions !== undefined) {
              const { id, dimensions } = change;
              setDimensionsNodes((prev) => ({ ...prev, [id]: dimensions }));
            }
            break;
          default:
            logger.log('NOT IMPLEMENTED: node change:', change);
            break;
        }
      }
    },
    [dispatch, draggingNodes, currentlySelectedEdges]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      for (const change of changes) {
        switch (change.type) {
          case 'select': {
            if (change.selected) {
              dispatch(
                updateNodesEdgesSelection({
                  edges: { selected: [change.id], deselected: [] },
                })
              );
            } else {
              dispatch(
                updateNodesEdgesSelection({
                  edges: { selected: [], deselected: [change.id] },
                })
              );
            }
            break;
          }
          default:
            logger.log('NOT IMPLEMENTED: edge change:', change);
            break;
        }
      }
    },
    [dispatch]
  );

  // NOTE(IL, 2024-05-07): passing ReactFlow the onEdgeUpdate prop is required to add EdgeAnchor to end(s) of edges
  const onEdgeUpdate = useCallback<OnEdgeUpdateFunc>(
    (oldEdge, newConnection) => {
      if (isTimeConstraintConnection(newConnection)) {
        const patch = timeConstraintConnectionToPartialInstance(newConnection);
        if (patch !== null) {
          edgeUpdateSuccessful.current = true;
          dispatch(
            editTimeConstraintConnection({
              id: oldEdge.id,
              patch,
            })
          );
        }
      } else if (isLabwareDependencyConnection(newConnection)) {
        edgeUpdateSuccessful.current = true;
        dispatch(
          thunkCreateLabwareDependencyEdge(newConnection, enqueueSnackbar)
        );
      } else if (isDataConnection(newConnection)) {
        edgeUpdateSuccessful.current = true;
        dispatch(thunkCreateDataDependencyEdge(newConnection, oldEdge));
      } else {
        // NOTE(IL, 2024-07-16): this is a plausible user input, eg trying to connect labware handle to time constraint. We ignore it.
        logger.log(
          'Connection is not a valid time constraint or labware dependency, ignoring edge update.'
        );
      }
    },
    [dispatch, enqueueSnackbar]
  );

  const onEdgeUpdateStart = useCallback(() => {
    edgeUpdateSuccessful.current = false;
  }, []);

  const onEdgeUpdateEnd = useCallback(
    (_event: MouseEvent | TouchEvent, edge: Edge) => {
      if (!edgeUpdateSuccessful.current) {
        // delete the edge after a user grabbed its EdgeAnchor
        // and dropped it anywhere that wasn't a valid handle
        dispatch(deleteNodesEdges({ edgeIds: [edge.id] }));
      }
    },
    [dispatch]
  );

  const onConnectStart: OnConnectStart = useCallback(
    (e, params) => {
      const { currentTarget } = e;
      if (currentTarget instanceof HTMLElement) {
        const isTimeConstraintHandle =
          currentTarget.dataset.handletype === TIME_CONSTRAINT_HANDLE_TYPE;
        if (isTimeConstraintHandle) {
          dispatch(setCurrentTimeConstraintConnectionInProgress(params));
        }
      }
    },
    [dispatch]
  );

  const onConnectEnd = useCallback(() => {
    dispatch(setCurrentTimeConstraintConnectionInProgress(null));
  }, [dispatch]);

  const onConnect = useCallback(
    (ctn: Connection) => {
      const connection = validateConnection(ctn);
      if (connection === null) {
        return;
      }

      const { connection: typedConnection, type } =
        getConnectionType(connection);

      switch (type) {
        case ConnectionType.TIME_CONSTRAINT: {
          const timeConstraint = getTimeConstraint(typedConnection);
          const id = generateTimeConstraintId(
            timeConstraint.source,
            timeConstraint.target
          );

          dispatch(createTimeConstraint({ ...timeConstraint, id }));

          dispatch(
            updateNodesEdgesSelection({
              edges: { selected: [id], deselected: currentlySelectedEdges },
              nodes: { selected: [], deselected: currentlySelectedNodeIds },
            })
          );

          dispatch(setNewlyCreatedTimeConstraintId(id));

          dispatch(setCurrentTimeConstraintConnectionInProgress(null));
          dispatch(changeSelectionDragState(false));
          break;
        }
        case ConnectionType.LABWARE_DEPENDENCY: {
          dispatch(
            thunkCreateLabwareDependencyEdge(typedConnection, enqueueSnackbar)
          );
          break;
        }
        case ConnectionType.DATA_DEPENDENCY: {
          dispatch(thunkCreateDataDependencyEdge(typedConnection));
          break;
        }
        default:
          logger.log(
            'Connection is not a valid time constraint or labware dependency, ignoring onConnect.'
          );
          break;
      }
    },
    [
      dispatch,
      enqueueSnackbar,
      currentlySelectedEdges,
      currentlySelectedNodeIds,
    ]
  );

  useEffect(() => {
    const activeTimeConstraintIds = new Set(
      transientEdges
        .filter((edge) => edge.type === TIME_CONSTRAINT_EDGE)
        .map((edge) => edge.id)
    );

    setTimeConstraintNodePositions((prev) => {
      const filteredEntries = Object.entries(prev).filter(([id]) =>
        activeTimeConstraintIds.has(id)
      );
      if (filteredEntries.length === Object.keys(prev).length) {
        return prev;
      }
      return Object.fromEntries(filteredEntries);
    });

    setCustomPositionedTimeConstraints((prev) => {
      const filteredEntries = Object.entries(prev).filter(([id]) =>
        activeTimeConstraintIds.has(id)
      );
      if (filteredEntries.length === Object.keys(prev).length) {
        return prev;
      }
      return Object.fromEntries(filteredEntries);
    });
  }, [transientEdges]);

  return {
    transientEdges,
    transientNodes,
    onNodesChange,
    onEdgesChange,
    onEdgeUpdate,
    onEdgeUpdateStart,
    onEdgeUpdateEnd,
    onConnectStart,
    onConnectEnd,
    onConnect,
  };
};
